import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, X, CalendarDays, Users } from "lucide-react";
import CourseSessionsModal from "./CourseSessionsModal";
import CourseEnrollmentModal from "./CourseEnrollmentModal";

const empty = {
  name: "",
  category: "music",
  department_slug: "",
  teacher_id: "",
  price_per_lesson: 0,
  teacher_share: 0,
  academy_share: 0,
  lessons_per_session: 1,
  billing_period: "monthly",
  weekday: "",
  time: "",
  location: "",
  active: true,
};

const Courses = () => {
  const [items, setItems] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sessionCourse, setSessionCourse] = useState(null);
  const [enrollCourse, setEnrollCourse] = useState(null);

  const load = () => api.get("/admin/courses").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/admin/teachers").then((r) => setTeachers(r.data));
  }, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (it) => { setForm({ ...empty, ...it }); setEditing(it); };
  const close = () => setEditing(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        price_per_lesson: Number(form.price_per_lesson) || 0,
        teacher_share: Number(form.teacher_share) || 0,
        academy_share: Number(form.academy_share) || 0,
        lessons_per_session: Math.max(1, parseInt(form.lessons_per_session) || 1),
        active: !!form.active,
      };
      if (editing === "new") await api.post("/admin/courses", payload);
      else await api.put(`/admin/courses/${editing.id}`, payload);
      toast.success("Gespeichert");
      close();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unbekannter Fehler";
      toast.error(`Fehler beim Speichern: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Kurs "${it.name}" wirklich löschen? Termine und Einschreibungen bleiben bestehen, aber Beträge können nicht mehr berechnet werden.`)) return;
    try {
      await api.delete(`/admin/courses/${it.id}`);
      toast.success("Gelöscht");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unbekannter Fehler";
      toast.error(`Löschen fehlgeschlagen: ${detail}`);
    }
  };

  const teacherName = (id) => teachers.find((t) => t.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="admin-courses">
      <Toaster position="top-center" theme="light" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif-display text-3xl">Kurse</h1>
          <p className="text-sm text-muted-foreground">{items.length} Einträge</p>
        </div>
        <button onClick={openNew} data-testid="courses-new-btn" className="inline-flex items-center gap-2 btn-gold px-5 py-2.5 rounded-full text-sm">
          <Plus className="w-4 h-4" /> Neu
        </button>
      </div>

      <div className="card-academy rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 border-b border-border">
              <tr>
                {["Kurs", "Kategorie", "Lehrer", "Wochentag", "Zeit", "Preis CHF", "Lekt./Termin", "Aktiv", "Aktion"].map((h) =>
                  <th key={h} className="text-left text-xs uppercase tracking-[0.15em] text-muted-foreground px-4 py-3">{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-border/50 hover:bg-secondary/20">
                  <td className="px-4 py-3 font-medium">{it.name}</td>
                  <td className="px-4 py-3">{it.category === "music" ? "Musik" : "Tanz"}</td>
                  <td className="px-4 py-3 text-xs">{teacherName(it.teacher_id)}</td>
                  <td className="px-4 py-3">{it.weekday || "—"}</td>
                  <td className="px-4 py-3">{it.time || "—"}</td>
                  <td className="px-4 py-3">{it.price_per_lesson}</td>
                  <td className="px-4 py-3">{it.lessons_per_session || 1}</td>
                  <td className="px-4 py-3 text-xs">{it.active ? <span className="text-primary">Ja</span> : <span className="text-muted-foreground">Nein</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <button
                        onClick={() => setEnrollCourse(it)}
                        data-testid={`course-enroll-${it.id}`}
                        title="Schüler zuweisen"
                        className="text-muted-foreground hover:text-primary inline-flex items-center gap-1 text-xs"
                      >
                        <Users className="w-4 h-4" />
                        <span className="hidden md:inline">Schüler</span>
                      </button>
                      <button
                        onClick={() => setSessionCourse(it)}
                        data-testid={`course-sessions-${it.id}`}
                        title="Termine verwalten"
                        className="text-muted-foreground hover:text-primary inline-flex items-center gap-1 text-xs"
                      >
                        <CalendarDays className="w-4 h-4" />
                        <span className="hidden md:inline">Termine</span>
                      </button>
                      <button onClick={() => openEdit(it)} data-testid={`course-edit-${it.id}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(it)} data-testid={`course-delete-${it.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Keine Kurse</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" role="dialog" data-testid="course-modal" onClick={close}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="font-serif-display text-xl">{editing === "new" ? "Neuer Kurs" : form.name}</div>
              <button type="button" onClick={close} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Kursname" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Kategorie</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  <option value="music">Musik</option>
                  <option value="dance">Tanz</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Lehrperson</label>
                <select
                  value={form.teacher_id || ""}
                  onChange={(e) => setForm({ ...form, teacher_id: e.target.value })}
                  data-testid="course-teacher-select"
                  className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary"
                >
                  <option value="">– keine Zuweisung –</option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}{t.department ? ` · ${t.department}` : ""}</option>
                  ))}
                </select>
              </div>
              <Field label="Preis pro Schüler / Lektion (CHF)" type="number" value={form.price_per_lesson} onChange={(v) => setForm({ ...form, price_per_lesson: v })} required />
              <Field label="Lektionen pro Termin (z.B. 2 = Doppellektion)" type="number" value={form.lessons_per_session} onChange={(v) => setForm({ ...form, lessons_per_session: v })} />
              <Field label="Anteil Lehrer / Lektion (CHF)" type="number" value={form.teacher_share} onChange={(v) => setForm({ ...form, teacher_share: v })} />
              <Field label="Anteil Academy / Lektion (CHF)" type="number" value={form.academy_share} onChange={(v) => setForm({ ...form, academy_share: v })} />
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Abrechnung</label>
                <select value={form.billing_period} onChange={(e) => setForm({ ...form, billing_period: e.target.value })}
                  className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  <option value="monthly">Monatlich</option>
                  <option value="quarterly">Quartalsweise</option>
                </select>
              </div>
              <Field label="Fachbereich Slug" value={form.department_slug} onChange={(v) => setForm({ ...form, department_slug: v })} />
              <Field label="Wochentag (Anzeige)" value={form.weekday} onChange={(v) => setForm({ ...form, weekday: v })} />
              <Field label="Uhrzeit (Anzeige)" value={form.time} onChange={(v) => setForm({ ...form, time: v })} />
              <Field label="Ort" full value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
              <label className="inline-flex items-center gap-2 md:col-span-2">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[hsl(35_90%_55%)]" />
                <span className="text-sm">Aktiv</span>
              </label>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-3">
              <button type="button" onClick={close} className="px-5 py-2 text-sm border border-border rounded-full hover:border-muted-foreground">Abbrechen</button>
              <button type="submit" disabled={loading} data-testid="course-save-btn" className="px-6 py-2 text-sm rounded-full btn-gold disabled:opacity-50">{loading ? "…" : "Speichern"}</button>
            </div>
          </form>
        </div>
      )}

      {sessionCourse && (
        <CourseSessionsModal course={sessionCourse} onClose={() => setSessionCourse(null)} />
      )}
      {enrollCourse && (
        <CourseEnrollmentModal course={enrollCourse} onClose={() => setEnrollCourse(null)} />
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", required, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{label}</label>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary"
    />
  </div>
);

export default Courses;
